import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Database, BarChart3, Workflow, Users, ShoppingCart, FileText,
  Boxes, Factory, Truck, Calculator, Wallet, LineChart, ArrowRight,
} from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";
import { TopRelatedDigitalItems } from "@/components/sections/top-related-digital-items";

export const Route = createFileRoute("/software/erp/")({
  component: ErpIndex,
  head: () => ({
    meta: [
      { title: "ERP Software — CoderLity" },
      { name: "description", content: "End-to-end enterprise resource planning for finance, inventory, manufacturing, sales and HR." },
      { property: "og:title", content: "ERP Software — CoderLity" },
      { property: "og:description", content: "Modern, modular ERP that unifies your business on one source of truth." },
      { property: "og:url", content: "/software/erp" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/software/erp" }],
  }),
});

type Item = { slug: string; title: string; desc: string; icon: typeof Boxes };
const modules: Item[] = [
  { slug: "inventory", title: "Inventory", desc: "Explore inventory", icon: Boxes },
  { slug: "sales-crm", title: "Sales & CRM", desc: "Explore sales & crm", icon: Users },
  { slug: "procurement", title: "Procurement", desc: "Explore procurement", icon: Truck },
  { slug: "manufacturing", title: "Manufacturing", desc: "Explore manufacturing", icon: Factory },
];
const finance: Item[] = [
  { slug: "accounting", title: "Accounting", desc: "Explore accounting", icon: Calculator },
  { slug: "payroll", title: "Payroll", desc: "Explore payroll", icon: Wallet },
  { slug: "reporting", title: "Reporting", desc: "Explore reporting", icon: LineChart },
];

function Group({ title, items }: { title: string; items: Item[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.slug}
                to="/software/erp/$child"
                params={{ child: it.slug }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                  {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ErpIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Modules" items={modules} />
        <Group title="Finance" items={finance} />
      </PageShell>
      <TopRelatedDigitalItems hub="erp" />
    </>
  );
}

const data = {
  eyebrow: "Software",
  title: "ERP Software",
  sub: "End-to-end resource planning for any business.",
  intro:
    "Run your entire business on one platform — finance, inventory, manufacturing, projects, sales, purchasing and HR. Our ERP is modular, cloud-native and configurable in days instead of months. Replace the spreadsheets, the disconnected tools and the legacy on-prem systems with a single source of truth your whole team actually uses.",
  features: [
    { icon: Database, title: "Unified data", description: "One database for customers, products, finances and operations." },
    { icon: Workflow, title: "Workflow automation", description: "Approvals, recurring billing and auto-generated journal entries." },
    { icon: FileText, title: "Accounting", description: "Double-entry ledger, multi-currency and tax-ready reporting." },
    { icon: ShoppingCart, title: "Inventory & procurement", description: "Multi-warehouse stock, reorder points and supplier management." },
    { icon: Users, title: "CRM + sales", description: "Pipeline, quotes, orders and customer history in one view." },
    { icon: BarChart3, title: "Real-time dashboards", description: "Cash flow, P&L, inventory and project health at a glance." },
  ],
  process: [
    { step: "01", title: "Discovery", description: "Map current processes, integrations and pain points." },
    { step: "02", title: "Configure", description: "Modules, workflows, chart of accounts and roles." },
    { step: "03", title: "Migrate", description: "Import data from QuickBooks, Xero, Excel or legacy ERP." },
    { step: "04", title: "Train & launch", description: "Team training, parallel run and go-live support." },
  ],
  deliverables: [
    "Fully configured ERP platform",
    "Migrated historical data with reconciliation",
    "Custom workflows and approval chains",
    "Integrations with banking, e-commerce and shipping",
    "Role-based dashboards and reports",
    "Team training and process documentation",
  ],
  techStack: ["ERPNext", "Odoo", "PostgreSQL", "Supabase", "Stripe", "QuickBooks API", "Shopify API", "Xero API"],
  pricing: [
    { name: "Starter", price: "$8k", features: ["Finance + inventory", "Up to 10 users", "Standard integrations", "4 weeks"] },
    { name: "Business", price: "$25k", period: "project", highlighted: true, features: ["All core modules", "Custom workflows", "Data migration", "8–12 weeks"] },
    { name: "Managed ERP", price: "$3k", period: "month", features: ["Hosting + backups", "Ongoing customization", "User support", "Quarterly upgrades"] },
  ],
  faqs: [
    { q: "Off-the-shelf or custom?", a: "We start with proven platforms (ERPNext, Odoo) and customize only where you have real differentiation — much faster and cheaper than full custom." },
    { q: "Can it replace QuickBooks?", a: "Yes — and add inventory, CRM, HR and project management on top. We migrate your chart of accounts and historical transactions." },
    { q: "Does it support multi-company and multi-currency?", a: "Both out of the box, with consolidated reporting and inter-company transactions." },
    { q: "Do you provide ongoing support?", a: "Yes. Our managed ERP plans include hosting, backups, user support and quarterly feature upgrades." },
  ],
};
